import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils/cn";
import { Spinner } from "./Spinner";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "outline";
type Size = "sm" | "md" | "lg";

const VARIANTS: Record<Variant, string> = {
  primary: "border border-accent/60 bg-accent text-accent-ink hover:border-accent-bright hover:bg-accent-bright active:bg-accent-dim font-semibold shadow-sm hover:shadow-lift",
  secondary: "border border-ink-500 bg-ink-700 text-haze-100 hover:bg-ink-600 font-medium",
  ghost: "text-haze-300 hover:text-haze-100 hover:bg-ink-700",
  danger: "bg-danger/15 text-danger hover:bg-danger/25 border border-danger/40",
  outline: "border border-ink-500 bg-ink-800/60 text-haze-100 hover:border-accent hover:bg-accent/5 hover:text-accent",
};

const SIZES: Record<Size, string> = {
  sm: "h-10 px-3.5 text-xs gap-1.5",
  md: "h-11 px-4 text-sm gap-2",
  lg: "h-12 px-6 text-base gap-2.5",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", loading, icon, fullWidth, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center rounded-xl transition-all duration-150",
        "disabled:opacity-45 disabled:pointer-events-none whitespace-nowrap",
        VARIANTS[variant],
        SIZES[size],
        fullWidth && "w-full",
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={size === "lg" ? 18 : 14} /> : icon}
      {children}
    </button>
  );
});
